import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createNotifications, createNotificationType, deleteNotifications, deleteNotificationType, getAdminNotifications, getNotificationById, getNotifications, getNotificationTypes, markAllNotificationsAsRead, markNotificationAsRead, updateNotifications, updateNotificationType, getNotificationLogs, getNotificationLogStats } from "../api/notifications";

export const useGetNotificationTypes = () => {
    return useQuery({
        queryKey: ["notification-types"],
        queryFn: () => getNotificationTypes(),
    });
}

export const useUpdateNotificationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["update-notification-type"],
        mutationFn: (data) => updateNotificationType(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["notification-types"]);
        },
    });
}

export const useDeleteNotificationType = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationKey: ["delete-notification-type"],
        mutationFn: (id) => deleteNotificationType(id),
        onSuccess: () => {
            queryClient.invalidateQueries(["notification-types"]);
        },
    });
}

export const useCreateNotificationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["create-notification-type"],
        mutationFn: (data) => createNotificationType(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["notification-types"]);
        },
    });
}

export const useGetNotifications = () => {
    return useQuery({
        queryKey: ["notifications"],
        queryFn: () => getNotifications(),
    });
}

export const useUpdateNotifications = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["update-notifications"],
        mutationFn: (data) => updateNotifications(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["notifications"]);
        },
    });
}

export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["delete-notification"],
        mutationFn: (id) => deleteNotifications(id),
        onSuccess: () => {
            queryClient.invalidateQueries(["notifications"]);
        },
    });
}

export const useCreateNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["create-notification"],
        mutationFn: (data) => createNotifications(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["notifications"]);
        },
    });
}

export const useGetNotificationById = (id) => {
    return useQuery({
        queryKey: ["notification", id],
        queryFn: () => getNotificationById(id),
        enabled: !!id, // Only run this query if id is provided
    });
}

export const useGetAdminNotifications = () => {
    return useQuery({
        queryKey: ["admin-notifications"],
        queryFn: () => getAdminNotifications(),
    });
}

export const useMarkNotificationAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["mark-notification-as-read"],
        mutationFn: (id) => markNotificationAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries(["admin-notifications"]);
        },
    });
}

export const useMarkAllNotificationsAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["mark-all-notifications-as-read"],
        mutationFn: () => markAllNotificationsAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries(["admin-notifications"]);
        },
    });
}

export const useGetNotificationLogs = (filters = {}, page = 1, limit = 10) => {
    return useQuery({
        queryKey: ["notification-logs", filters, page, limit],
        queryFn: () => getNotificationLogs({ ...filters, page, limit }),
        keepPreviousData: true,
    });
}

export const useGetNotificationLogStats = () => {
    return useQuery({
        queryKey: ["notification-log-stats"],
        queryFn: () => getNotificationLogStats(),
    });
}